import type { Patient } from "services/types/patients.type";
import { formatMoney } from "utils/common";

import {
  activeTags,
  appointmentContext,
  balanceDue,
  pendingFormsCount
} from "./softphone-patient-context.util";

// The folded call bar has one 12px line under the name, about seventeen
// characters wide after the state word. Rather than pick one fact and lose
// the rest, the line steps through them in words (ENG-2226): the first step
// says what the phone is doing, the ones after it the things that change
// how the call is handled, most useful first. Tag COLOURS never appear here —
// a colour on its own says nothing without the tag's name.

// "calling" is an outbound call the far end has not picked up yet.
export type CallBarState = "ringing" | "calling" | "active" | "held";

export type BarFactsInput = {
  state: CallBarState;
  // The live timer for a call that is up ("3:12"); the SDK formats it.
  duration?: string | null;
  patient?: Patient | null;
  // The colleague who transferred the call, when it was transferred.
  transferredBy?: string | null;
  // "Transferring…" / "Transferred to Jane" while a transfer is in flight.
  transferStatus?: string | null;
  // Who this is when the caller is not a patient.
  caller?: { kind: "contact"; label: string } | { kind: "caller-id" } | null;
  // Several patients share the number and none has been picked yet.
  sharedPatientCount?: number;
  noteSaved?: boolean;
  // The in-call "Private" toggle is on (ENG-2364).
  privateCall?: boolean;
};

const RINGING_WORD = "Incoming";
const MAX_TAG_FACTS = 4;

function patientFacts(patient: Patient | null | undefined) {
  if (!patient) return [];
  const facts: string[] = [];
  // Each tag is its own step, newest first, as many as the card shows.
  for (const assigned of activeTags(patient).slice(0, MAX_TAG_FACTS)) {
    const name = assigned.tag?.name?.trim();
    if (name) facts.push(name);
  }
  const appointment = appointmentContext(patient);
  if (appointment) {
    facts.push(
      appointment.shortWhen.startsWith("Today")
        ? `Appt ${appointment.shortWhen.replace("Today", "today")}`
        : `Appt ${appointment.shortWhen}`
    );
  }
  const balance = balanceDue(patient);
  if (balance > 0) facts.push(`${formatMoney(balance, 2)} due`);
  const forms = pendingFormsCount(patient);
  if (forms > 0) facts.push(`${forms} form${forms > 1 ? "s" : ""} pending`);
  return facts;
}

function callerFacts(input: BarFactsInput) {
  const facts: string[] = [];
  if (input.sharedPatientCount && input.sharedPatientCount > 1) {
    facts.push(`${input.sharedPatientCount} patients`);
  }
  if (input.caller?.kind === "contact") facts.push(input.caller.label);
  if (input.caller?.kind === "caller-id") facts.push("Caller ID");
  return facts;
}

export function barFacts(input: BarFactsInput): string[] {
  const timer = input.duration ? ` · ${input.duration}` : "";
  // A held caller is the one state that never steps away from itself: the
  // amber line stays put so nobody forgets them.
  if (input.state === "held") return [`On hold${timer}`];

  if (input.state === "calling") {
    return ["Calling…", ...patientFacts(input.patient), ...callerFacts(input)];
  }

  if (input.state === "active") {
    const facts = [`On a call${timer}`];
    if (input.privateCall) facts.push("Private call");
    if (input.transferStatus) facts.push(input.transferStatus);
    if (input.noteSaved) facts.push("Note saved");
    facts.push(...patientFacts(input.patient), ...callerFacts(input));
    return facts;
  }

  const facts: string[] = [];
  if (input.transferredBy) facts.push(`Transfer from ${input.transferredBy}`);
  facts.push(...patientFacts(input.patient), ...callerFacts(input));
  // The state word rides on the first step only; the ones after it get the
  // whole width, and the pulsing photo keeps saying "ringing".
  if (facts.length === 0) return [`${RINGING_WORD} call`];
  return [`${RINGING_WORD} · ${facts[0]}`, ...facts.slice(1)];
}
